import React, { useCallback } from "react";
import { TemplateCapableFieldComponent } from "./types";
import { QueryBuilderComponent } from "@syncfusion/ej2-react-querybuilder";

// Takes a given FieldV2 component and returns a template for it
export function makeTemplateField<TProps, TValue>(
  Component: React.ComponentType<
    TProps & TemplateCapableFieldComponent<TValue>
  >,
  queryBuilderComponentRef: React.RefObject<QueryBuilderComponent | null>,
  fieldName: string,
  props: TProps
) {
  return (args: any) => {
    const transformString = (s: string) => {
      if (args.rule.operator === "between") {
        return new Date(s);
      }
      return s.replace(/^['"]|['"]$/g, "");
    };
    const initialValue = Array.isArray(args.rule.value)
      ? args.rule.value
          .map((v: TValue) => (typeof v === "string" ? transformString(v) : v))
          .filter((v: TValue) => v !== null)
      : [];

    const [fieldValue, setFieldValue] = React.useState<TValue>(initialValue);

    const onSubmit = useCallback(
      (newValue: TValue) => {
        setFieldValue(newValue);

        const updatedValue = Array.isArray(newValue)
          ? newValue.length > 0
            ? typeof newValue[0] === "string"
              ? newValue.map((v: TValue) => `'${v}'`)
              : typeof newValue[0] === "object" && newValue[0] instanceof Date
              ? newValue.map((v: Date) => v.toLocaleDateString())
              : newValue
            : [null]
          : newValue;

        // Update the rule value
        if (args && args.rule && queryBuilderComponentRef.current) {
          // Get the current rule from the QueryBuilder
          const currentRule = queryBuilderComponentRef.current.rule;

          // Find and update the specific rule that matches our field
          const updateRule = (rule: any): any => {
            if (
              rule.field === fieldName &&
              rule.operator === args.rule.operator
            ) {
              return {
                ...rule,
                value: updatedValue,
              };
            }
            if (rule.rules && Array.isArray(rule.rules)) {
              return {
                ...rule,
                rules: rule.rules.map(updateRule),
              };
            }
            return rule;
          };

          // Update the rule and trigger change
          const updatedRule = updateRule(currentRule);

          const newRule = {
            condition: updatedRule.properties.condition,
            rules: updatedRule.rules,
          };

          queryBuilderComponentRef.current.setRules(updatedRule);

          // Trigger the ruleChange event
          queryBuilderComponentRef.current?.trigger("ruleChange", {
            rule: newRule,
          });
        }
      },
      [fieldName, args.rule.operator]
    );

    return (
      <Component
        {...props}
        onSubmit={onSubmit}
        value={fieldValue}
        key={fieldName}
      />
    );
  };
}
